import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  PlanTier,
  SubscriptionStatus,
  SubscriptionSource,
  PlanLimits,
  UsageQuota,
} from '@affogato/shared';
import { subscriptionApi } from '@/api/subscription';

interface SubscriptionState {
  // State
  planTier: PlanTier;
  status: SubscriptionStatus;
  planLimits: PlanLimits | null;
  usage: UsageQuota | null;
  canAccessPremiumFeatures: boolean;
  // Expiration tracking
  currentPeriodEnd: string | null;
  manualExpiresAt: string | null;
  source: SubscriptionSource | null;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchStatus: () => Promise<void>;
  reset: () => void;
  // Expiration helpers
  getExpirationDate: () => Date | null;
  getDaysUntilExpiration: () => number | null;
  isExpiringSoon: (days?: number) => boolean;
}

const initialState = {
  planTier: 'starter' as PlanTier,
  status: 'pending' as SubscriptionStatus,
  planLimits: null,
  usage: null,
  canAccessPremiumFeatures: false,
  currentPeriodEnd: null,
  manualExpiresAt: null,
  source: null as SubscriptionSource | null,
  isLoading: false,
  error: null,
};

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      ...initialState,

      fetchStatus: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await subscriptionApi.getStatus();
          // Handle null subscription (user has no license)
          const sub = response.data.subscription;
          set({
            planTier: sub?.planTier || 'starter',
            status: sub?.status || 'pending', // 'pending' indicates no access
            planLimits: response.data.planLimits,
            usage: response.data.usage,
            canAccessPremiumFeatures: response.data.canAccessPremiumFeatures,
            currentPeriodEnd: sub?.currentPeriodEnd || null,
            manualExpiresAt: sub?.manualExpiresAt || null,
            source: sub?.source || null,
            isLoading: false,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Error al cargar suscripción';
          set({ error: message, isLoading: false });
        }
      },

      reset: () => set(initialState),

      getExpirationDate: (): Date | null => {
        const state = get();
        const dateStr = state.source === 'google_play'
          ? state.currentPeriodEnd
          : state.manualExpiresAt;
        return dateStr ? new Date(dateStr) : null;
      },

      getDaysUntilExpiration: (): number | null => {
        const expirationDate = get().getExpirationDate();
        if (!expirationDate) return null;
        const now = new Date();
        const diffMs = expirationDate.getTime() - now.getTime();
        return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      },

      isExpiringSoon: (days = 7): boolean => {
        const daysUntil = get().getDaysUntilExpiration();
        if (daysUntil === null) return false;
        return daysUntil <= days && daysUntil > 0;
      },
    }),
    {
      name: 'affogato-subscription',
      partialize: (state) => ({
        planTier: state.planTier,
        status: state.status,
        planLimits: state.planLimits,
        usage: state.usage, // Persist usage to maintain limits after reload
        canAccessPremiumFeatures: state.canAccessPremiumFeatures,
        currentPeriodEnd: state.currentPeriodEnd,
        manualExpiresAt: state.manualExpiresAt,
        source: state.source,
      }),
    }
  )
);

// Helper hooks
export const useIsPremium = () => {
  const { canAccessPremiumFeatures } = useSubscriptionStore();
  return canAccessPremiumFeatures;
};

export const usePlanLimits = () => {
  const { planLimits } = useSubscriptionStore();
  return planLimits;
};

export const useCanUseFeature = (feature: 'clients' | 'diagnostics' | 'ai_uses') => {
  const { planLimits, usage, canAccessPremiumFeatures } = useSubscriptionStore();

  if (canAccessPremiumFeatures) {
    return { allowed: true, remaining: null, limit: null };
  }

  if (!planLimits || !usage) {
    return { allowed: true, remaining: null, limit: null };
  }

  let limit: number | null = null;
  let used = 0;

  switch (feature) {
    case 'clients':
      limit = planLimits.clients;
      used = usage.clientsCreated;
      break;
    case 'diagnostics':
      limit = planLimits.diagnostics;
      used = usage.diagnosticsCreated;
      break;
    case 'ai_uses':
      limit = planLimits.aiUsesPerMonth;
      used = usage.aiUses;
      break;
  }

  // -1 means unlimited
  if (limit === null || limit === -1) {
    return { allowed: true, remaining: null, limit: null };
  }

  const remaining = Math.max(0, limit - used);
  return {
    allowed: remaining > 0,
    remaining,
    limit,
  };
};
